import { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { FOCO_SOBRE_AZUL } from 'utils/foco';

import { numeroPtBR } from '../minha-equipe-calculos';

export interface TotaisDoPanorama {
  /** Realizações cadastradas pela equipe no ciclo vigente (MEQ2-01). */
  realizacoes: number | null;
  selos: number | null;
  reconhecimentos: number | null;
}

/** Contagens das ações do líder; null = leitura indisponível (o cartão fica sem rodapé de contagem). */
export interface PendenciasDoLider {
  validacao: number | null;
  autorizacao: number | null;
}

export interface PanoramaDaEquipeProps {
  /** Título do ciclo vigente ("2S2026"). */
  ciclo: string | null;
  diretoria: string;
  /** `range.total` do `equipe_gestor`. */
  totalDeLiderados: number | null;
  totais: TotaisDoPanorama;
  /** Ciclo fechado dos selos ("1S2026"), ou null sem ciclo fechado. */
  cicloSelos: string | null;
  pendencias: PendenciasDoLider;
  /** `textoDoPrazo` do calendário; null esconde o texto. */
  prazo: string | null;
  mostrarValidar: boolean;
  mostrarParticipantes: boolean;
  /** Cartão "Apreciar equipe lateral" (quem não é da Rede, MEQ2-03). */
  mostrarEquipeLateral?: boolean;
  /** Abre o seletor de equipe lateral. */
  onVerEquipeLateral?: () => void;
  /**
   * Na equipe lateral, o gestor no lugar de quem as ações agem (MEQ2-07): os cartões levam à fila dele e não
   * mostram as contagens da própria equipe.
   */
  gestorSubstituido?: string | null;
  /**
   * Um cartão abriu a fila de validar ou de participantes nesta aba (a página mantém a lateralidade para ela, MEQ2-07).
   * Abrir em outra aba (Ctrl/Cmd, Shift, botão do meio) não conta: esta página continua aberta.
   */
  onAbrirFila?: () => void;
}

const SEM_CONTAGEM: PendenciasDoLider = { validacao: null, autorizacao: null };

export const ROTA_VALIDAR = '/minha-equipe/validar-apreciar-realizacao';
export const ROTA_PARTICIPANTES = '/minha-equipe/participantes-da-realizacao';

function textoDasPendencias({ validacao, autorizacao }: PendenciasDoLider): string | null {
  if (validacao === null && autorizacao === null) return null;
  const total = (validacao ?? 0) + (autorizacao ?? 0);
  if (total === 0) return 'Nenhuma pendência para você';
  return `${total} ${total === 1 ? 'pendência' : 'pendências'} para você`;
}

const SETA = (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--brand-blue)"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    className="ml-auto transition-transform duration-200 group-hover:translate-x-1 motion-reduce:group-hover:translate-x-0"
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const ICONE_VALIDAR = (
  <svg
    width="23"
    height="23"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--brand-blue-dark)"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" />
    <path d="m9 14 2 2 4-4" />
  </svg>
);

const ICONE_PARTICIPANTES = (
  <svg
    width="23"
    height="23"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--brand-navy)"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 3 4 6v5c0 5 3.4 9.2 8 10 4.6-.8 8-5 8-10V6z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const ICONE_EQUIPE_LATERAL = (
  <svg
    width="23"
    height="23"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--brand-navy)"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/** Todo cartão leva a algum lugar: uma rota (link) ou, sem ela, um botão que chama `aoClicar` (abre um modal). */
type DestinoDoCartao = { para: string; aoClicar?: () => void } | { para?: undefined; aoClicar: () => void };

type CartaoDeAcaoProps = DestinoDoCartao & {
  icone: ReactNode;
  fundoDoIcone: string;
  titulo: string;
  descricao: string;
  /** null = sem contagem: o rodapé mostra só `rodape` (ou nada) e a seta. */
  quantidade: number | null;
  /** "aguardando sua validação" / "aguardando sua autorização". */
  pendencia?: string;
  /** Texto fixo do rodapé, para o cartão sem contagem ("Visualizar equipe lateral"). */
  rodape?: string;
};

/** O clique que o `Link` leva a navegar nesta aba: botão principal, sem tecla que abre outra aba ou janela. */
function navegaNestaAba(evento: MouseEvent) {
  return evento.button === 0 && !evento.metaKey && !evento.ctrlKey && !evento.shiftKey && !evento.altKey;
}

const CLASSE_DO_CARTAO = `group flex flex-col gap-2.5 rounded-jj-lg bg-brand-white px-[22px] pb-[18px] pt-5 text-left font-text text-content no-underline shadow-[0_2px_10px_rgba(17,24,39,.10)] transition hover:-translate-y-[3px] hover:text-content hover:no-underline hover:shadow-[0_14px_30px_rgba(0,45,75,0.14)] focus:text-content focus:no-underline active:translate-y-0 motion-reduce:hover:translate-y-0 ${FOCO_SOBRE_AZUL}`;

function CartaoDeAcao({ para, aoClicar, icone, fundoDoIcone, titulo, descricao, quantidade, pendencia, rodape }: CartaoDeAcaoProps) {
  const conteudo = (
    <>
      <span className={`flex h-11 w-11 items-center justify-center rounded-jj-md ${fundoDoIcone}`}>{icone}</span>
      <span className="font-display text-base font-bold leading-[normal] text-brand-blue-dark">{titulo}</span>
      <span className="text-[12.5px] leading-[1.55] text-content-soft [text-wrap:pretty]">{descricao}</span>
      <span className="mt-auto flex w-full items-center gap-2 border-t border-surface-200 pt-3">
        {quantidade !== null &&
          (quantidade > 0 ? (
            // SPEC_DEVIATION: rosa em `text-urgent` (o `brand-pink` dá 2,9:1 no branco); o ponto mantém o rosa do design.
            <span className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-urgent">
              <span className="h-2 w-2 rounded-full bg-brand-pink" aria-hidden="true" />
              {quantidade} {pendencia}
            </span>
          ) : (
            <span className="text-[11.5px] font-bold text-brand-blue">Nada aguardando você</span>
          ))}
        {quantidade === null && rodape && <span className="text-[11.5px] font-bold text-brand-blue">{rodape}</span>}
        {SETA}
      </span>
    </>
  );

  if (para) {
    return (
      <Link
        to={para}
        onClick={evento => {
          if (navegaNestaAba(evento)) aoClicar?.();
        }}
        className={CLASSE_DO_CARTAO}
      >
        {conteudo}
      </Link>
    );
  }

  return (
    <button type="button" onClick={aoClicar} className={CLASSE_DO_CARTAO}>
      {conteudo}
    </button>
  );
}

/** Painel azul do topo: números da equipe no ciclo, pendências do líder e os cartões de ação. */
export default function PanoramaDaEquipe({
  ciclo,
  diretoria,
  totalDeLiderados,
  totais,
  cicloSelos,
  pendencias,
  prazo,
  mostrarValidar,
  mostrarParticipantes,
  mostrarEquipeLateral = false,
  onVerEquipeLateral,
  gestorSubstituido = null,
  onAbrirFila,
}: PanoramaDaEquipeProps) {
  const numeros = [
    { valor: totalDeLiderados, rotulo: 'Funcionários sob sua supervisão' },
    { valor: totais.realizacoes, rotulo: 'Realizações cadastradas' },
    { valor: totais.selos, rotulo: cicloSelos ? `Selos conquistados · ${cicloSelos}` : 'Selos conquistados' },
    { valor: totais.reconhecimentos, rotulo: 'Reconhecimentos recebidos' },
  ];
  const resumoDasPendencias = textoDasPendencias(pendencias);
  const temAcoes = mostrarValidar || mostrarParticipantes || mostrarEquipeLateral;
  // Na equipe lateral, as contagens da própria equipe não valem para os cartões.
  const contagens = gestorSubstituido ? SEM_CONTAGEM : pendencias;

  return (
    <section aria-labelledby="titulo-panorama" className="rounded-jj-panel bg-brand-blue px-7 pb-6 pt-[26px]">
      <div className="mb-[18px] flex flex-wrap items-baseline gap-3">
        <h2 id="titulo-panorama" className="m-0 font-display text-[22px] font-extrabold leading-[normal] text-brand-yellow">
          Panorama da sua equipe
        </h2>
        {/* SPEC_DEVIATION: branco pleno (o design usa 88%, 4,2:1 sobre o azul). */}
        {ciclo && (
          <p className="m-0 text-xs leading-[normal] text-brand-white">
            Ciclo {ciclo} · {diretoria}
          </p>
        )}
      </div>

      <dl className="m-0 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {numeros.map(numero => (
          <div key={numero.rotulo} className="flex flex-col-reverse justify-end rounded-jj-lg bg-brand-white px-[18px] py-4">
            <dt className="mt-1.5 text-[11.5px] font-normal leading-[normal] text-content-soft">{numero.rotulo}</dt>
            <dd className="m-0 font-display text-[30px] font-extrabold leading-none text-brand-blue">
              {numeroPtBR(numero.valor)}
            </dd>
          </div>
        ))}
      </dl>

      {(resumoDasPendencias || prazo) && (
        <div className="mt-4 flex flex-wrap items-center gap-2.5 text-[12.5px] text-brand-white">
          {resumoDasPendencias && (
            <span className="rounded-jj-pill bg-brand-yellow px-3 py-[5px] font-display text-xs font-bold leading-[normal] text-brand-blue-dark">
              {resumoDasPendencias}
            </span>
          )}
          {prazo && <span>{prazo}</span>}
        </div>
      )}

      {temAcoes && (
        <>
          <h3 className="m-0 mb-2.5 mt-[22px] text-xs font-normal leading-[normal] text-brand-white">Ações do líder nesta fase</h3>
          {gestorSubstituido && (
            <p className="m-0 mb-2.5 text-[12.5px] leading-[normal] text-brand-white">
              Você está na equipe de <b>{gestorSubstituido}</b>: apreciar e avaliar participantes acontecem no lugar desse
              gestor, em lateralidade ou substituição.
            </p>
          )}
          {/* Como no design: tantas colunas quantas couberem, de no mínimo 240px. */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3.5">
            {mostrarValidar && (
              <CartaoDeAcao
                para={ROTA_VALIDAR}
                aoClicar={onAbrirFila}
                icone={ICONE_VALIDAR}
                fundoDoIcone="bg-brand-yellow-light"
                titulo="Validar/apreciar realização"
                descricao="Analise as entregas cadastradas pela sua equipe, deixe feedback e valide ou devolva para ajuste."
                quantidade={contagens.validacao}
                pendencia="aguardando sua validação"
              />
            )}
            {mostrarParticipantes && (
              <CartaoDeAcao
                para={ROTA_PARTICIPANTES}
                aoClicar={onAbrirFila}
                icone={ICONE_PARTICIPANTES}
                fundoDoIcone="bg-brand-purple-light"
                titulo="Avaliar nº de participantes"
                descricao="Autorize a quantidade nas realizações coletivas com mais de 20 participantes, conforme sua alçada."
                quantidade={contagens.autorizacao}
                pendencia="aguardando sua autorização"
              />
            )}
            {mostrarEquipeLateral && onVerEquipeLateral && (
              <CartaoDeAcao
                aoClicar={onVerEquipeLateral}
                icone={ICONE_EQUIPE_LATERAL}
                fundoDoIcone="bg-brand-cyan-light"
                titulo="Apreciar equipe lateral"
                descricao="Acompanhe e aprecie as realizações dos funcionários que você lidera em lateralidade ou substituição."
                quantidade={null}
                rodape="Visualizar equipe lateral"
              />
            )}
          </div>
        </>
      )}
    </section>
  );
}
